import React, { useEffect, useState } from "react";
import { isInternalUser, trackEvent } from "../../atlas-utils";
import { connect } from "react-redux";
import NotificationServices from "../../services/NotificationService";

const MerakiLanding = ({ biz, user }) => {
	const initialState = {
		hero: false,
		benefits: false,
		branding: false,
		marketing: false,
		checkout: false
	};

	const merakiEventFired = JSON.parse(localStorage.getItem("meraki_event_fired")) ?? {
		hero: false,
		benefits: false,
		branding: false,
		marketing: false,
		checkout: false
	};

	const pqlEmailDetails = JSON.parse(localStorage.getItem("pql-email-sent")) ?? {};

	const [animationDetails, setAnimationDetails] = useState(initialState);
	const [isBannerPresent, setIsBannerPresent] = useState(false);
	const [pqlEmailSent, setPQLEmailSent] = useState(pqlEmailDetails);
	const [eventFiredDetails, setEventFiredDetails] = useState(merakiEventFired);

	useEffect(() => {
		const observer = setUpObserver();
		registerPostHogEvent("meraki_landing_page_visit");

		window.addEventListener("beforeunload", handleReload);
		return () => {
			observer.disconnect();
			window.removeEventListener("beforeunload", handleReload);
		};
	}, []);

	useEffect(() => {
		const observer = setUpBannerObserver();

		return () => {
			observer.disconnect();
		};
	}, []);

	const handleReload = () => {
		localStorage.removeItem("meraki_event_fired");
	};

	const setUpObserver = () => {
		const options = {
			root: document.querySelector("#observer-container"),
			rootMargin: "0px",
			threshold: 0.4
		};
		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					const dataSet = entry.target.dataset["id"];
					if (dataSet) {
						registerPostHogEvent("meraki_scroll_depth", { section: `${dataSet}_section` }, dataSet);
						updateAnimationDetails(dataSet);
					}
				}
			});
		}, options);

		const targetElements = document.querySelectorAll(".landing-page-section");

		targetElements.forEach((element) => {
			observer.observe(element);
		});
		return observer;
	};

	const setUpBannerObserver = () => {
		const config = { childList: true, subtree: true };
		const observer = new MutationObserver(() => {
			const banner = document.querySelector(".banner-message") || document.querySelector(".atlas-top-banner");
			if (banner) {
				setIsBannerPresent(true);
			} else {
				setIsBannerPresent(false);
			}
		});

		observer.observe(document.body, config);
		return observer;
	};

	const updateAnimationDetails = (dataset) => {
		setAnimationDetails((prev) => {
			if (!prev[dataset]) {
				const details = { ...prev };
				details[dataset] = true;
				return details;
			}
			return prev;
		});
	};

	const registerPostHogEvent = (eventName, metaData, section) => {
		if (eventName === "meraki_landing_page_visit") {
			trackEvent(eventName, { ...metaData });
			return;
		}

		const storedEvents = JSON.parse(localStorage.getItem("meraki_event_fired")) || {};

		if (!storedEvents[section]) {
			storedEvents[section] = true;
			trackEvent(eventName, { ...metaData });
			localStorage.setItem("meraki_event_fired", JSON.stringify(storedEvents));
			setEventFiredDetails(storedEvents);
		}
	};

	const interestedInMeraki = async (section) => {
		const body = {
			biz_id: biz.id,
			biz_name: biz.name,
			country: biz.country,
			user_name: user.fullName,
			email: user.email,
			phone: user.phone || 0o0,
			date: new Date().toString(),
			menu_v2_enabled: biz.isMenuOverCatalogueEnabled,
			multi_brand: biz.isMultibrandEnabled,
			section
		};

		try {
			const token = user.token;
			const MERAKI_API_URL = process.env.REACT_APP_MERAKI_API_URL;
			const response = await fetch(`${MERAKI_API_URL}/web/interested`, {
				method: "POST",
				headers: { Authorization: token, "Content-Type": "application/json" },
				body: JSON.stringify(body)
			});

			const result = await response.json();
			if (result.status) {
				trackEvent("meraki_cta_clicked", { section_clicked_from: section });
				NotificationServices.pushNotification({
					message: "Interest submitted. We’ll follow up soon!",
					timeout: 3000,
					type: "success",
					isClosable: true,
					theme: "dark"
				});

				const pqlEmailDetails = JSON.parse(localStorage.getItem("pql-email-sent")) || {};
				pqlEmailDetails[biz.id] = true;
				setPQLEmailSent(pqlEmailDetails);
				localStorage.setItem("pql-email-sent", JSON.stringify(pqlEmailDetails));
			} else {
				NotificationServices.pushNotification({
					message: "We're having trouble processing your request right now. Please try again later.",
					timeout: 3000,
					type: "warning",
					isClosable: true,
					theme: "dark"
				});
			}
		} catch (error) {
			console.error("Error - Interested in Meraki: ", error);
			NotificationServices.pushNotification({
				message: "Oops! Something went wrong. Please try again in a moment.",
				timeout: 3000,
				type: "error",
				isClosable: true,
				theme: "dark"
			});
		}
	};

	return (
		<div id="observer-container" style={!isBannerPresent ? { height: "91vh" } : {}}>
			<HeroSection
				animateComplete={animationDetails["hero"]}
				interestedInMeraki={interestedInMeraki}
				pqlEmailSent={pqlEmailSent}
				biz={biz}
			/>
			<KeyBenefitsSection animateComplete={animationDetails["benefits"]} />
			<CustomBranding
				animateComplete={animationDetails["branding"]}
				interestedInMeraki={interestedInMeraki}
				pqlEmailSent={pqlEmailSent}
				biz={biz}
			/>
			<Marketing
				animateComplete={animationDetails["marketing"]}
				interestedInMeraki={interestedInMeraki}
				pqlEmailSent={pqlEmailSent}
				biz={biz}
			/>
			<Checkout
				animateComplete={animationDetails["checkout"]}
				interestedInMeraki={interestedInMeraki}
				pqlEmailSent={pqlEmailSent}
				biz={biz}
			/>
		</div>
	);
};

const HeroSection = ({ animateComplete, interestedInMeraki, pqlEmailSent, biz }) => {
	return (
		<div data-id="hero" className="landing-page-section hero-section-container">
			<div className={`hero-wrapper ${animateComplete ? "animate-complete" : ""}`}>
				<div className="hero-section-left">
					<div className="hero-section-left-sub">
						<div className="title">
							<span role="img" aria-label="star">
								⭐
							</span>{" "}
							Take control of your brand, customers, and margins.
						</div>

						<h1 className="header online-ordering">
							<span>Your Own Online Ordering System –</span>{" "}
							<span className="blue">Zero Commissions</span>
						</h1>

						<p>
							Boost profits and customer loyalty with your own branded website. No middlemen, no
							commissions—just direct online orders you control.
						</p>

						{isInternalUser() ? null : !pqlEmailSent[biz.id] ? (
							<button onClick={() => interestedInMeraki("Hero")} className="at-btn demo-btn">
								Interested? Let’s Chat
							</button>
						) : (
							<Noted />
						)}
					</div>
				</div>
				<div className="hero-section-right">
					<div className="hero-section-right-sub">
						<div className="images-container">
							<img
								className="image-one"
								src="/assets/meraki/pages/landing/homepage-with-updated-cart.png"
								alt=""
							/>
							<div className="image-two-container">
								<img
									className="image-two"
									src="/assets/meraki/pages/landing/homepage-mobile.png"
									alt=""
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

const KeyBenefitsSection = ({ animateComplete }) => {
	const benefitsData = [
		{
			id: 1,
			iconSrc: "/assets/meraki/pages/landing/percent.svg",
			title: "No Commission Fees",
			desc: "Keep 100% of your revenue—no commissions, no third-party cuts."
		},
		{
			id: 2,
			iconSrc: "/assets/meraki/pages/landing/website-icon.svg",
			title: "Branded Website & App",
			desc: "Launch an SEO-optimised website and app, fully customised for your brand."
		},
		{
			id: 3,
			iconSrc: "/assets/meraki/pages/landing/integrations-icon.svg",
			title: "Seamless Integrations",
			desc: "Connect effortlessly with POS systems, payment gateways, and delivery partners."
		},
		{
			id: 4,
			iconSrc: "/assets/meraki/pages/landing/data-icon.svg",
			title: "Own Your Customer Data",
			desc: "Gain full access to customer insights to drive loyalty and repeat orders."
		},
		{
			id: 5,
			iconSrc: "/assets/meraki/pages/landing/promotions-icon.svg",
			title: "Marketing & Promotions",
			desc: "Drive growth with built-in tools for promotions, loyalty, and discounts."
		},
		{
			id: 6,
			iconSrc: "/assets/meraki/pages/landing/location-support-icon.svg",
			title: "Multi-Location Support",
			desc: "Manage all your locations & menus from one unified dashboard."
		}
	];

	return (
		<div
			data-id="benefits"
			className={`landing-page-section benefits-section-container ${animateComplete ? "animate-complete" : ""}`}
		>
			<div className={`benefits-sub-container ${animateComplete ? "animate-complete" : ""}`}>
				<h1 className="header">
					<span className="blue">Key Benefits</span> – Why Choose Meraki?
				</h1>
				<div className="benefits-wrapper">
					{benefitsData.map((data) => (
						<div key={data.id} className="benefit-card">
							<div>
								<img src={data.iconSrc} alt="" />
							</div>
							<h5>{data.title}</h5>
							<p>{data.desc}</p>
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

const CustomBranding = ({ animateComplete, interestedInMeraki, pqlEmailSent, biz }) => {
	return (
		<div data-id="branding" className="landing-page-section branding-section-container">
			<div className="branding-sub-container">
				<div className="branding-section-left-sub">
					<div className={`left-sub-wrapper ${animateComplete ? "animation-complete" : ""}`}>
						<h1 className="header custom-branding-title">
							Built for,{" "}
							<div className="meraki-features">
								<div className="meraki-features-scroll">
									<span className="blue">Branded Storefronts</span>
									<span>Instant Setup</span>
									<span>Seamless Checkouts</span>
									<span className="blue">Branded Storefronts</span>
									<span>Instant Setup</span>
									<span>Seamless Checkouts</span>
								</div>
							</div>
						</h1>
						<p className="branding-description">
							Tailor your ordering experience to match your brand's identity—colors, logos, layouts, and
							more. Enjoy full UI flexibility, fast checkout flows, and menu controls that adapt to your
							business needs.
						</p>

						{isInternalUser() ? null : !pqlEmailSent[biz.id] ? (
							<button onClick={() => interestedInMeraki("Branding")} className="at-btn demo-btn">
								Interested? Let’s Chat
							</button>
						) : (
							<Noted />
						)}
					</div>
				</div>
				<div className={`branding-section-right-sub ${animateComplete ? "animation-complete" : ""}`}>
					<div className="branding-img-background">
						<img
							className={`image-one ${animateComplete ? "animation-complete" : ""}`}
							src="/assets/meraki/pages/landing/meraki-screen-landscape.png"
							alt=""
						/>
						<img
							className={`image-two ${animateComplete ? "animation-complete" : ""}`}
							src="/assets/meraki/pages/landing/meraki-color-pallet.png"
							alt=""
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

const Marketing = ({ animateComplete, interestedInMeraki, pqlEmailSent, biz }) => {
	return (
		<div data-id="marketing" className="landing-page-section marketing-section-container">
			<div className={`marketing-sub-container ${animateComplete ? "animation-complete" : ""}`}>
				<div className="marketing-section-left-sub">
					<div className="marketing-left-sub-wrapper">
						<img
							className={`phone ${animateComplete ? "animation-complete" : ""}`}
							src="/assets/meraki/pages/landing/landing-phone.png"
							alt=""
						/>
						<img
							className={`campain ${animateComplete ? "animation-complete" : ""}`}
							src="/assets/meraki/pages/landing/campain-settings.png"
							alt=""
						/>
						<img
							className={`notification ${animateComplete ? "animation-complete" : ""}`}
							src="/assets/meraki/pages/landing/landing-notification.svg"
							alt=""
						/>
						<img
							className={`message ${animateComplete ? "animation-complete" : ""}`}
							src="/assets/meraki/pages/landing/landing-message.png"
							alt=""
						/>
					</div>
				</div>
				<div className="marketing-section-right-sub">
					<h1 className="header">
						Turn First-Time Customers into <span className="blue">Loyal Fans</span>
					</h1>
					<p>
						Engage your customers with automated campaigns, loyalty rewards, and personalised offers —
						delivered via email, SMS, or app notifications. Drive repeat orders and build long-term customer
						relationships with ease.
					</p>
					{isInternalUser() ? null : !pqlEmailSent[biz.id] ? (
						<button onClick={() => interestedInMeraki("Marketing")} className="at-btn demo-btn">
							Interested? Let’s Chat
						</button>
					) : (
						<Noted />
					)}
				</div>
			</div>
		</div>
	);
};

const Checkout = ({ animateComplete, interestedInMeraki, pqlEmailSent, biz }) => {
	return (
		<div data-id="checkout" className="landing-page-section checkout-section-container">
			<div className="checkout-sub-container">
				<div className="checkout-section-left-sub">
					<div className={`checkout-left-sub-wrapper ${animateComplete ? "animation-complete" : ""}`}>
						<h1 className="header">
							<span className="blue">Effortless</span> Checkout, <span className="blue">Reliable</span>{" "}
							Deliveries
						</h1>
						<p>
							Offer a frictionless ordering experience with multiple payment methods and trusted delivery
							partners—ensuring fast, reliable fulfillment every time.
						</p>
						{isInternalUser() ? null : !pqlEmailSent[biz.id] ? (
							<button onClick={() => interestedInMeraki("Checkout")} className="at-btn demo-btn">
								Interested? Let’s Chat
							</button>
						) : (
							<Noted />
						)}
					</div>
				</div>
				<div className="checkout-section-right-sub">
					<div className="checkout-right-sub-wrapper">
						<img
							className={`phone ${animateComplete ? "animation-complete" : ""}`}
							src="/assets/meraki/pages/landing/landing-mcd-order-phone.png"
							alt=""
						/>
						<img
							className={`checkout ${animateComplete ? "animation-complete" : ""}`}
							src="/assets/meraki/pages/landing/landing-checkout-btn-bar.svg"
							alt=""
						/>
						<img
							className={`notification ${animateComplete ? "animation-complete" : ""}`}
							src="/assets/meraki/pages/landing/landing-order-notification.svg"
							alt=""
						/>
						<img
							className={`razor-pay ${animateComplete ? "animation-complete" : ""}`}
							src="/assets/meraki/pages/landing/landing-razor-pay.svg"
							alt=""
						/>
						<img
							className={`pay-tabs ${animateComplete ? "animation-complete" : ""}`}
							src="/assets/meraki/pages/landing/landing-pay-tabs.svg"
							alt=""
						/>
						<img
							className={`stripe ${animateComplete ? "animation-complete" : ""}`}
							src="/assets/meraki/pages/landing/landing-stripe.svg"
							alt=""
						/>
						<img
							className={`wallet ${animateComplete ? "animation-complete" : ""}`}
							src="/assets/meraki/pages/landing/landing-wallet-icon.svg"
							alt=""
						/>
						<img
							className={`scooter ${animateComplete ? "animation-complete" : ""}`}
							src="/assets/meraki/pages/landing/landing-scooter-icon.svg"
							alt=""
						/>
						<img
							className={`shadow-fax ${animateComplete ? "animation-complete" : ""}`}
							src="/assets/meraki/pages/landing/landing-shadow-fax.svg"
							alt=""
						/>
						<img
							className={`add-logs ${animateComplete ? "animation-complete" : ""}`}
							src="/assets/meraki/pages/landing/landing-add-logs.svg"
							alt=""
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

const Noted = () => {
	return (
		<div className="pql-sent">
			<img src="/assets/periscope/verified.svg" className="pql-sent-img" />
			<div>
				<div className="pql-sent-bold">We’ve noted your interest!</div>
				<div className="pql-sent-para">Our team will reach out soon</div>
			</div>
		</div>
	);
};

const mapStateToProp = (store) => ({
	biz: store.login.loggedInbizDetail,
	user: store.login.loginDetail
});

export default connect(mapStateToProp)(MerakiLanding);
